import {
  ref,
  computed,
  inject,
  onMounted,
  provide,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from "vue";
import type { DRDrawing } from "@/types";
import { drawingRepository } from "@/repositories/drawing.repository";
import APIClient from "@/APIClient";

export interface DrawingDiagnosticLink {
  drawing: Ref<DRDrawing | undefined>;
  /** Chargement initial — sans cet état, l'onglet flasherait « non lié ». */
  isLoadingDrawing: Ref<boolean>;
  diagnosticId: ComputedRef<number>;
  loadDrawing: () => Promise<void>;
}

const DRAWING_DIAGNOSTIC_LINK_KEY: InjectionKey<DrawingDiagnosticLink> =
  Symbol("drawingDiagnosticLink");

/**
 * Charge le lien plan ↔ ouvrage UNE fois pour tout un volet et le partage à
 * ses onglets (`useDrawingDiagnosticLink` le retrouve par injection) : passer
 * d'un onglet à l'autre remonte son panneau, mais ne relit plus le plan ni
 * n'attend le serveur.
 */
export function provideDrawingDiagnosticLink(
  drawingId: number,
): DrawingDiagnosticLink {
  const link = createDrawingDiagnosticLink(drawingId);
  provide(DRAWING_DIAGNOSTIC_LINK_KEY, link);
  return link;
}

/**
 * Lien plan ↔ ouvrage côté canvas : les onglets Observations, IA et Parties
 * ne peuvent écrire d'observations que si le plan est rattaché à un ouvrage
 * (`Drawing.diagnosticId`). Reprend le lien fourni par le volet parent quand
 * il existe, sinon le charge pour ce composant.
 */
export function useDrawingDiagnosticLink(
  drawingId: number,
): DrawingDiagnosticLink {
  return (
    inject(DRAWING_DIAGNOSTIC_LINK_KEY, null) ??
    createDrawingDiagnosticLink(drawingId)
  );
}

function createDrawingDiagnosticLink(drawingId: number): DrawingDiagnosticLink {
  const drawing = ref<DRDrawing | undefined>();
  const isLoadingDrawing = ref(true);

  /**
   * Dexie d'abord (offline-first), puis rafraîchi depuis le serveur : le record
   * local peut être antérieur à la liaison plan↔ouvrage (le sync ne remplace le
   * record que quand la révision change). Pas de gate sur `isOnline` : il vaut
   * `null` tant que le monitor n'a pas tourné (ex. arrivée directe sur le
   * canvas) — le try/catch couvre le vrai offline.
   */
  const loadDrawing = async (): Promise<void> => {
    drawing.value = await drawingRepository.getById(drawingId);
    try {
      const [fresh] = await APIClient.fetchDrawings({
        ids: [drawingId],
        archived: false,
        skip: 0,
        take: 1,
        sortBy: "createdAt",
        order: "desc",
      });
      if (fresh) {
        drawing.value = fresh;
        await drawingRepository.replace(fresh);
      }
    } catch {
      // offline / serveur injoignable — on garde le record local
    }
  };

  onMounted(async () => {
    try {
      await loadDrawing();
    } finally {
      isLoadingDrawing.value = false;
    }
  });

  const diagnosticId = computed(() => drawing.value?.diagnostic?.id ?? 0);

  return { drawing, isLoadingDrawing, diagnosticId, loadDrawing };
}
